// Helper function to determine review source consistently
export const getReviewSource = (review) => {
  // If source is already set, use it
  if (review.source) {
    return review.source;
  }
  
  // Otherwise infer based on properties
  if (review.author_name || review.relative_time_description) {
    return 'google'; // Google reviews have author_name or relative_time_description
  } 
  
  if (review.created_at) {
    return 'zahoot'; // Zahoot reviews have created_at
  }
  
  return 'unknown'; // Fallback
};

// Helper function to safely extract rating value
export const getReviewRating = (review) => {
  if (!review) return 0;
  
  // Try to parse the rating as a number
  if (review.rating !== undefined) {
    const rating = parseFloat(review.rating);
    if (!isNaN(rating)) {
      return rating;
    }
  }
  
  // Google reviews might have different rating fields
  if (review.stars !== undefined) {
    const stars = parseFloat(review.stars);
    if (!isNaN(stars)) {
      return stars;
    }
  }
  
  // For Google reviews specifically
  if (getReviewSource(review) === 'google') {
    // Google review ratings might not use expected fields
    // Try to infer from other data
    if (review.text && review.text.match(/^[1-5]\s*\/\s*5/)) {
      // Sometimes ratings might be at the start of the text like "4/5 Great lawyer..."
      const ratingMatch = review.text.match(/^([1-5])\s*\/\s*5/);
      if (ratingMatch) {
        return parseInt(ratingMatch[1]);
      }
    }
  }
  
  // Default to a middle rating if we can't determine
  return 3;
};

// Helper function to get review timestamp
export const getReviewTimestamp = (review) => {
  const reviewSource = getReviewSource(review);

  try {
    // For Google reviews, prioritize relative time description
    if (reviewSource === 'google' && review.relative_time_description) {
      // Extract time values from relative time descriptions like "a month ago", "3 weeks ago", etc.
      const now = Date.now();
      const rtd = review.relative_time_description.toLowerCase();
      
      // Match patterns like "3 months ago", "a week ago", etc.
      const match = rtd.match(/(\d+|a|an)\s+(second|minute|hour|day|week|month|year)s?\s+ago/);
      
      if (match) {
        const [_, amount, unit] = match;
        
        // Convert "a" or "an" to 1
        const numericAmount = amount === 'a' || amount === 'an' ? 1 : parseInt(amount, 10);
        
        // Convert to milliseconds
        let milliseconds = 0;
        switch (unit) {
          case 'second': milliseconds = numericAmount * 1000; break;
          case 'minute': milliseconds = numericAmount * 60 * 1000; break;
          case 'hour': milliseconds = numericAmount * 60 * 60 * 1000; break;
          case 'day': milliseconds = numericAmount * 24 * 60 * 60 * 1000; break;
          case 'week': milliseconds = numericAmount * 7 * 24 * 60 * 60 * 1000; break;
          case 'month': milliseconds = numericAmount * 30 * 24 * 60 * 60 * 1000; break;
          case 'year': milliseconds = numericAmount * 365 * 24 * 60 * 60 * 1000; break;
        }
        
        const timestamp = now - milliseconds;
        return timestamp;
      }
    }

    // For Zahoot reviews, use created_at
    if (reviewSource === 'zahoot' && review.created_at) {
      const timestamp = new Date(review.created_at).getTime();
      return timestamp;
    }

    // For Google reviews with time field (Unix timestamp)
    if (review.time) {
      // If time is already a number, use it directly
      if (typeof review.time === 'number') {
        return review.time;
      }
      
      // If time is a string that looks like a number
      if (typeof review.time === 'string' && /^\d+$/.test(review.time)) {
        const timestamp = parseInt(review.time, 10);
        return timestamp;
      }
    }
    
    // Last resort for any review type: if all we have is created_at
    if (review.created_at) {
      const timestamp = new Date(review.created_at).getTime();
      return timestamp;
    }

    // Fallback: create a deterministic "hash" from the review content
    let hashValue = 0;
    
    // Use author name, review text, and relative time to create a hash
    const authorName = review.author_name || review.name || '';
    const reviewText = review.text || '';
    const relativeTime = review.relative_time_description || '';
    const contentToHash = authorName + reviewText + relativeTime;
    
    for (let i = 0; i < contentToHash.length; i++) {
      // Simple hash function - not cryptographic
      hashValue = ((hashValue << 5) - hashValue) + contentToHash.charCodeAt(i);
      hashValue |= 0; // Convert to 32bit integer
    }
    
    // Use this hash value as a timestamp fallback
    const fallbackTimestamp = 1000000000000 + Math.abs(hashValue % 1000000000);
    return fallbackTimestamp;
  } catch (error) {
    console.error("Error getting review timestamp:", error);
    return 0; // Default value if all else fails
  }
};

// Calculate review counts by rating
export const getReviewCountsByRating = (reviewsArray) => {
  const counts = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0};
  
  if (!reviewsArray || reviewsArray.length === 0) {
    return counts;
  }
  
  reviewsArray.forEach(review => {
    const rating = Math.round(getReviewRating(review));
    if (rating >= 1 && rating <= 5) {
      counts[rating]++;
    }
  });
  
  return counts;
};

// Get unique review sources
export const getReviewSources = (reviewsArray) => {
  if (!reviewsArray || reviewsArray.length === 0) {
    return [];
  }
  
  const sources = new Set();
  reviewsArray.forEach(review => {
    sources.add(getReviewSource(review));
  });
  
  return Array.from(sources);
};

// Count reviews by source
export const getReviewCountsBySource = (reviewsArray) => {
  const counts = {};
  
  if (!reviewsArray || reviewsArray.length === 0) {
    return counts;
  }
  
  reviewsArray.forEach(review => {
    const source = getReviewSource(review);
    counts[source] = (counts[source] || 0) + 1;
  });
  
  return counts;
};

// Calculate average rating from all reviews (both Google and internal)
export const calculateAverageRating = (reviewsData) => {
  // If stats are available, use the pre-calculated average rating
  if (reviewsData && reviewsData.stats && reviewsData.stats.average_rating !== null) {
    return reviewsData.stats.average_rating;
  }
  
  // If we have the reviews array
  let reviewsArray = [];
  
  if (reviewsData && reviewsData.reviews && Array.isArray(reviewsData.reviews)) {
    reviewsArray = reviewsData.reviews;
  } else if (Array.isArray(reviewsData)) {
    reviewsArray = reviewsData;
  }
  
  if (reviewsArray.length === 0) {
    return null; // Return null instead of 0 for no reviews
  }
  
  // Calculate average from all reviews using the getReviewRating utility function
  const sum = reviewsArray.reduce((acc, review) => acc + getReviewRating(review), 0);
  return parseFloat((sum / reviewsArray.length).toFixed(1));
}; 